import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReportLayout, TwoColumnLayout } from '../../layouts';
import { Header } from '../../features/Report';
import { CtaClutchBadgeFullWidth, SidebarMenu, MobileNavMenu, PrimaryButton } from '../../components';
import { config } from '../../shared/config';
import { useSectionNavigation, useSeoReportData, useCtaContent } from '../../hooks/report';
import { buildHeaderDataFromSeoAuditData } from '../../features/Report/utils/headerData';
import {
  ContentSection,
  IndexingSection,
  MobileSection,
  StructuredDataSection,
  SecuritySection,
  PerformanceSection,
  TechnologiesSection,
  BrandingSection,
  DomainSection,
  BacklinksSection,
  TrafficSection,
  SocialProfilesSection,
  LocalSection
} from './sections';
import '../../features/Report/ReportTables.css';
import './ReportPage.css';

// Sidebar menu items base data (without isActive/onClick - added dynamically)
const sidebarMenuItemsBase = [
  { id: 'content', label: 'Content', isLoading: false },
  { id: 'indexing', label: 'Indexing', isLoading: false },
  { id: 'mobile', label: 'Mobile', isLoading: false },
  { id: 'structured-data', label: 'Structured Data', isLoading: false },
  { id: 'security', label: 'Security', isLoading: false },
  { id: 'performance', label: 'Performance', isLoading: false },
  { id: 'technologies', label: 'Technologies', isLoading: false },
  { id: 'branding', label: 'Branding', isLoading: false },
  { id: 'domain', label: 'Domain', isLoading: false },
  { id: 'backlinks', label: 'Backlinks', isLoading: false },
  { id: 'traffic', label: 'Traffic', isLoading: false },
  { id: 'social', label: 'Social Profiles', isLoading: false },
  { id: 'local', label: 'Local', isLoading: false },
];

// Mobile menu items base data (without onClick - added dynamically in component)
const mobileMenuItemsBase = [
  { id: 'summary', label: 'Summary' },
  { id: 'content', label: 'Content' },
  { id: 'indexing', label: 'Indexing' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'structured-data', label: 'Structured Data' },
  { id: 'security', label: 'Security' },
  { id: 'performance', label: 'Performance' },
  { id: 'technologies', label: 'Technologies' },
  { id: 'branding', label: 'Branding' },
  { id: 'domain', label: 'Domain' },
  { id: 'backlinks', label: 'Backlinks' },
  { id: 'traffic', label: 'Traffic' },
  { id: 'social', label: 'Social Profiles' },
  { id: 'local', label: 'Local' },
];

export const ReportPage = () => {
  const [searchParams] = useSearchParams();

  // Get query parameters
  const tokenFromUrl = searchParams.get('token');
  // Optional debug mode: force the error UI (and avoid any polling/API work).
  // Enable via: ?force_error (or ?force_error=1 / ?force_error=true)
  const isForceError = searchParams.has('force_error');
  const token = isForceError ? null : tokenFromUrl;
  const showMockLocalDirectories =
    searchParams.get('mock_local_directories') === '1' ||
    searchParams.get('mock_local_directories') === 'true';
  
  // Section IDs for navigation
  const sectionIds = useMemo(() => mobileMenuItemsBase.map((item) => item.id), []);
  
  // Use custom hooks
  const {
    mobileMenuItems,
    sidebarMenuItems,
  } = useSectionNavigation(sectionIds, sidebarMenuItemsBase, mobileMenuItemsBase);

  const {
    isPolling,
    isError,
    isTimeout,
    seoAuditData,
    contentSectionData,
    indexingSectionData,
    mobileSectionData,
    structuredDataSectionData,
    securitySectionData,
    performanceSectionData,
    technologiesSectionData,
    brandingSectionData,
    domainSectionData,
    backlinksSectionData,
    trafficSectionData,
    socialProfilesSectionData,
    localSectionData,
  } = useSeoReportData(token);

  // Fire the GTM/Segment event once when the server indicates this report generation
  // should trigger the "SEO Checker Report Generated" step.
  const hasFiredReportGeneratedEventRef = useRef(false);
  useEffect(() => {
    if (hasFiredReportGeneratedEventRef.current) return;
    if (!seoAuditData || typeof seoAuditData !== 'object') return;
    if (seoAuditData.shouldFireReportGeneratedEvent !== true) return;

    const notifyUser = seoAuditData.notifyUser && typeof seoAuditData.notifyUser === 'object'
      ? seoAuditData.notifyUser
      : {};

    // Match fetchSegmentAnonymousId logic: cookie first, then analytics.user().anonymousId()
    const getSegmentAnonymousId = () => {
      let id = '';
      if (typeof window !== 'undefined') {
        if (window.FX?.Tracking?.getCookie) {
          id = window.FX.Tracking.getCookie('ajs_anonymous_id') || '';
        }
        if (!id && window.analytics?.user) {
          const user = window.analytics.user();
          if (user && typeof user.anonymousId === 'function') {
            id = user.anonymousId() || '';
          }
        }
      }
      return id;
    };

    const fireEvent = (anonymousId) => {
      const seoCheckerResults = seoAuditData;
      const payload = {
        event: 'ran_seo_checker',
        event_step: 'SEO Checker Report Generated',
        event_type: 'react',
        url: seoAuditData.reportUrl || window.location.href,
        email: notifyUser.email || '',
        name: notifyUser.name || '',
        website: notifyUser.website || '',
        form: 'seo-review-form',
        anonymous_id: anonymousId || '',
        'seo-checker-results': seoCheckerResults
      };
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      }
      hasFiredReportGeneratedEventRef.current = true;
    };

    let anonymousId = getSegmentAnonymousId();

    if (anonymousId) {
      fireEvent(anonymousId);
      return;
    }

    // Retry up to 10 times at 500ms intervals when anonymousId is initially empty
    let retries = 10;
    const intervalId = setInterval(() => {
      anonymousId = getSegmentAnonymousId();
      if (anonymousId) {
        clearInterval(intervalId);
        fireEvent(anonymousId);
      } else if (--retries <= 0) {
        clearInterval(intervalId);
        fireEvent('');
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [seoAuditData]);

  // Optional debug mode: inject 2 fake local directories for UI preview.
  // Enable via: ?mock_local_directories=1
  const localSectionDataForRender = useMemo(() => {
    if (!showMockLocalDirectories) return localSectionData;

    const mockedLocalDirectories = {
      // Keep this neutral so it doesn't impact header counts (info is ignored).
      status: 'info',
      iconType: 'info',
      isEmpty: false,
      message: null,
      data: {
        listings: [
          {
            directoryName: 'Google Business Profile',
            name: 'Acme Plumbing',
            pageLink: 'https://example.com/google-business-profile/acme-plumbing',
            address: '123 Main St, Harrisburg, PA 17101',
            phone: '(717) 555-0100',
            reviewsCount: 128,
            reviewsLink: 'https://example.com/google-business-profile/acme-plumbing#reviews',
          },
          {
            directoryName: 'Yelp',
            name: 'Acme Plumbing',
            pageLink: 'https://example.com/yelp/acme-plumbing',
            address: '123 Main St, Harrisburg, PA 17101',
            phone: '(717) 555-0100',
            reviewsCount: 42,
            reviewsLink: 'https://example.com/yelp/acme-plumbing#reviews',
          },
        ],
      },
    };

    return {
      ...(localSectionData || {}),
      errorCount: (localSectionData && typeof localSectionData.errorCount === 'number') ? localSectionData.errorCount : 0,
      toImproveCount: (localSectionData && typeof localSectionData.toImproveCount === 'number') ? localSectionData.toImproveCount : 0,
      localDirectories: mockedLocalDirectories,
    };
  }, [showMockLocalDirectories, localSectionData]);

  const headerData = useMemo(() => {
    // In React polling, `seoAuditData` is typically the inner `data` object
    // (url/score/lastGeneratedAt/screenshot/criteria). The helper supports both shapes.
    return buildHeaderDataFromSeoAuditData(seoAuditData, {
      fallbackSiteUrl: searchParams.get('site_url') || '',
      sectionDataList: [
        contentSectionData,
        indexingSectionData,
        mobileSectionData,
        structuredDataSectionData,
        securitySectionData,
        performanceSectionData,
        technologiesSectionData,
        brandingSectionData,
        domainSectionData,
        backlinksSectionData,
        trafficSectionData,
        socialProfilesSectionData,
        localSectionDataForRender,
      ],
    });
  }, [
    seoAuditData,
    searchParams,
    contentSectionData,
    indexingSectionData,
    mobileSectionData,
    structuredDataSectionData,
    securitySectionData,
    performanceSectionData,
    technologiesSectionData,
    brandingSectionData,
    domainSectionData,
    backlinksSectionData,
    trafficSectionData,
    socialProfilesSectionData,
    localSectionDataForRender,
  ]);

  const headerScore = headerData?.score || 0;

  const {
    heading: ctaHeading,
    description: ctaDescription,
    buttonLabel: ctaButtonLabel,
    handleCtaSubmit,
  } = useCtaContent(headerScore);

  // Handle redirect to homepage
  const handleRequestNewReport = () => {
    window.location.href = config.homepageUrl;
  };

  // Show loading state while polling
  if (token && isPolling) {
    return (
      <div className="seo-checker-loading-shell">
        <div className="seo-checker-loading-shell__dots">
          <div className="seo-checker-loading-shell__dot"></div>
          <div className="seo-checker-loading-shell__dot"></div>
          <div className="seo-checker-loading-shell__dot"></div>
        </div>
        <p className="seo-checker-loading-shell__text">Loading your report...</p>
      </div>
    );
  }

  // Show error state (for any error scenario: missing token, API error, or timeout)
  if (isForceError || !tokenFromUrl || isError || isTimeout) {
    return (
      <div className="seo-checker-error">
        <div className="seo-checker-error__icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 0C37.2552 0 48 10.7472 48 24C48 37.2528 37.2552 48 24 48C10.7448 48 0 37.2528 0 24C0 10.7472 10.7448 0 24 0ZM24.0043 31.2089C22.6806 31.2089 21.6075 32.282 21.6075 33.6057C21.6075 34.9294 22.6806 36.0025 24.0043 36.0025C25.328 36.0025 26.4011 34.9294 26.4011 33.6057C26.4011 32.282 25.328 31.2089 24.0043 31.2089ZM23.9991 12C22.7683 12.0004 21.7543 12.9273 21.616 14.121L21.6 14.4009L21.6043 26.403L21.6206 26.6829C21.7596 27.8764 22.7744 28.8026 24.0052 28.8021C25.236 28.8017 26.2501 27.8748 26.3883 26.6811L26.4043 26.4012L26.4 14.3991L26.3838 14.1193C26.2447 12.9257 25.2299 11.9996 23.9991 12Z" fill="#CED4DA"/>
          </svg>
        </div>
        <div className="seo-checker-error__content">
          <h2 className="seo-checker-error__title">Something went wrong.</h2>
          <p className="seo-checker-error__message">
            {'Oops, an error occurred while trying to generate your report. Click below to request a new report or try again later.'}
          </p>
        </div>
        <button
          className="seo-checker-error__button"
          onClick={handleRequestNewReport}
        >
          Request a new report
        </button>
      </div>
    );
  }

  return (
    <ReportLayout>
      {/* Mobile Navigation Menu - Fixed at top on mobile */}
      <MobileNavMenu items={mobileMenuItems} />
      
      <div className="seo-checker-report-page">
        <TwoColumnLayout className="seo-checker-report-page__header"
          leftColumn={
            <div className="seo-checker-report-page__sidebar">
              {/* Placeholder for header/CTA left column */}
            </div>
          }
          rightColumn={
            <div id="summary" className="seo-checker-report-page__content">
              <Header
                siteUrl={headerData.siteUrl || ''}
                reportDate={headerData.reportDate || ''}
                score={headerData.score || 0}
                passed={headerData.passed || 0}
                toImprove={headerData.toImprove || 0}
                errors={headerData.errors || 0}
                screenshotUrl={headerData.screenshotUrl || ''}
              />
              <CtaClutchBadgeFullWidth
                heading={ctaHeading}
                description={ctaDescription}
                buttonLabel={ctaButtonLabel}
                siteUrl={headerData.siteUrl || ''}
                onSubmit={handleCtaSubmit}
              />
            </div>
          }
        />
        
        {/* Report Sections with Sidebar */}
        <TwoColumnLayout
          leftColumn={
              <SidebarMenu 
              items={sidebarMenuItems}
              isSticky={true}
              bottomContent={
                <PrimaryButton
                  onClick={() => {
                    const url = new URL('https://www.webfx.com/free-quote/');
                    const websiteUrl = (headerData.siteUrl || '').trim();
                    if (websiteUrl) {
                      url.searchParams.set('website', websiteUrl);
                    }
                    window.location.assign(url.toString());
                  }}
                >
                  Get Help
                </PrimaryButton>
              }
            />
          }
          rightColumn={
            <div className="seo-checker-report-page__sections">
              <ContentSection id="content" siteUrl={headerData.siteUrl || ''} data={contentSectionData} />
              <IndexingSection id="indexing" siteUrl={headerData.siteUrl || ''} data={indexingSectionData} />
              <MobileSection id="mobile" siteUrl={headerData.siteUrl || ''} data={mobileSectionData} />
              <StructuredDataSection id="structured-data" data={structuredDataSectionData} />
              <SecuritySection id="security" data={securitySectionData} />
              <PerformanceSection id="performance" data={performanceSectionData} />
              <TechnologiesSection id="technologies" data={technologiesSectionData} />
              <BrandingSection id="branding" data={brandingSectionData} />
              <DomainSection id="domain" data={domainSectionData} />
              <BacklinksSection id="backlinks" data={backlinksSectionData} />
              <TrafficSection id="traffic" data={trafficSectionData} />
              <SocialProfilesSection id="social" data={socialProfilesSectionData} />
              <LocalSection id="local" data={localSectionDataForRender} />
            </div>
          }
        />
      </div>
    </ReportLayout>
  );
};
